import { batch } from "omi";
import keyBy from "lodash-es/keyBy";

import { createStore } from "@/utils/store";
import { Variable } from "@/api";

import { UseVariableUpdate, useVariableUpdate } from "./mutations";

type State = {
  map: Record<string, Variable>;
  array: Variable[];
};

type Slices = {
  variableUpdate: UseVariableUpdate;
};

type Actions = {
  get: (name: string) => string | undefined;
  set: (name: string, value: string | undefined) => void;
  delete: (name: string) => void;
  setAll: (variables: Variable[]) => void;
  getAll: () => Variable[];
};

export type VariablesSlice = ReturnType<typeof createVariablesSlice>;

export function createVariablesSlice(initialVariables: Variable[]) {
  return createStore<State, {}, Actions, Slices>({
    initialState: {
      array: initialVariables,
      map: keyBy(initialVariables, "name"),
    },
    actions: ({ store, slices }) => ({
      getAll: () => store.array.value,
      get: name => {
        return store.map.value[name]?.value;
      },
      set: (name, value) => {
        slices.variableUpdate.mutate(name, value ?? "");

        return batch(() => {
          store.map.value[name] = { name, value };
          store.array.value = Object.values(store.map.value);
          store.map.update();
        });
      },
      setAll: variables =>
        batch(() => {
          store.map.value = keyBy(variables, "name");
          store.array.value = Object.values(store.map.value);
          store.map.update();
        }),
      delete: name =>
        batch(() => {
          delete store.map.value[name];
          store.map.update();
          store.array.value = Object.values(store.map.value);
        }),
    }),
    slices: () => ({
      variableUpdate: useVariableUpdate(),
    }),
    networkState: {},
  });
}
